import {
    BORDER_GRAY,
    NEUTRAL_BLUE,
    SURFACE_GRAY,
} from '@/components/decisionSupport/DecisionSupport.styled';
import { FactorScore } from '@/components/decisionSupport/tables/RecommendationsTable.constants';
import { TableCell, TableRow, styled } from '@mui/material';

// TODO (FE-253): Refactor Tanstack table implementation to be more reusable.

// Cell styles
export const CELL_FONT_SIZE = '12px';
export const ROW_ACTIVE_BACKGROUND_COLOR = 'rgba(0, 0, 0, 0.08)';
export const CELL_BOTTOM_GAP_PX = 8;
export const CELL_VERTICAL_PADDING_PX = 8;
export const CELL_WIDTH_PX = 100;

// Header styles
export const HEADER_BACKGROUND_COLOR = '#FFFFFF';
export const HEADER_FONT_SIZE = '11px';
export const HEADER_LINE_HEIGHT = 1.2;
export const HEADER_FONT_WEIGHT = 400;

// Skeleton sizes
export const SKELETON_COL_COUNT = 7;
export const SKELETON_ROW_COUNT = 5;
export const SKELETON_ROW_HEIGHT = '20px';

// Cell backgrounds by tier / state
export const FACTOR_SCORE_BACKGROUNDS: Record<FactorScore, string> = {
    [FactorScore.STRONG_POSITIVE]: '#2E9F6E80',
    [FactorScore.POSITIVE]: '#2E9F6E33',
    [FactorScore.NEUTRAL]: '#F59E0B33',
    [FactorScore.NEGATIVE]: '#D6454533',
    [FactorScore.STRONG_NEGATIVE]: '#D6454566',
    [FactorScore.NONE]: 'transparent',
};

export const NOT_AVAILABLE_CELL_BACKGROUND = '#9CA3AF';

export const CONSTRAINT_CELL_BACKGROUNDS: Record<'satisfied' | 'unsatisfied', string> = {
    satisfied: FACTOR_SCORE_BACKGROUNDS[FactorScore.STRONG_POSITIVE],
    unsatisfied: FACTOR_SCORE_BACKGROUNDS[FactorScore.STRONG_NEGATIVE],
};

// TableCell base:
// vertical divider on the right, removed on last cell.
const StyledDividerCell = styled(TableCell)`
    border-right: 1px solid ${BORDER_GRAY};
    &:last-child {
        border-right: none;
    }
`;

// Body row cell:
// isLast removes the white bottom gap + pushes cell fill to the floor.
// isFirst keeps the name column from wrapping.
type StyledRowCellProps = {
    isLast: boolean;
    isFirst: boolean;
};

export const StyledRowCell = styled(StyledDividerCell, {
    shouldForwardProp: (prop) => prop !== 'isLast' && prop !== 'isFirst',
})<StyledRowCellProps>`
    position: relative;
    padding-top: ${CELL_VERTICAL_PADDING_PX}px;
    padding-bottom: ${CELL_VERTICAL_PADDING_PX}px;
    background-color: ${SURFACE_GRAY};
    border-bottom: none;
    ${({ isFirst }) =>
        !isFirst &&
        `min-width: ${CELL_WIDTH_PX}px; max-width: ${CELL_WIDTH_PX}px; width: ${CELL_WIDTH_PX}px;`}
    box-shadow: ${({ isLast }) => (isLast ? 'none' : `inset 0 -${CELL_BOTTOM_GAP_PX}px 0 #FFFFFF`)};
    ${({ isLast }) =>
        isLast &&
        `
        padding-top: ${CELL_VERTICAL_PADDING_PX - CELL_BOTTOM_GAP_PX / 2}px;
        padding-bottom: ${CELL_VERTICAL_PADDING_PX - CELL_BOTTOM_GAP_PX / 2}px;
        & > div { bottom: 0; }
    `}
    ${({ isFirst, isLast }) =>
        isFirst &&
        !isLast &&
        `
        padding-top: ${CELL_VERTICAL_PADDING_PX - CELL_BOTTOM_GAP_PX / 2}px;
        padding-bottom: ${CELL_VERTICAL_PADDING_PX + CELL_BOTTOM_GAP_PX / 2}px;
    `}
    ${({ isFirst }) => isFirst && `white-space: nowrap;`}
`;

// Header cell
type StyledHeaderCellProps = {
    isFirst: boolean;
};

export const StyledHeaderCell = styled(StyledDividerCell, {
    shouldForwardProp: (prop) => prop !== 'isFirst',
})<StyledHeaderCellProps>`
    font-size: ${HEADER_FONT_SIZE};
    line-height: ${HEADER_LINE_HEIGHT};
    font-weight: ${HEADER_FONT_WEIGHT};
    color: ${NEUTRAL_BLUE};
    background-color: ${HEADER_BACKGROUND_COLOR};
    border-bottom: none;
    ${({ isFirst }) =>
        isFirst
            ? `white-space: nowrap;`
            : `min-width: ${CELL_WIDTH_PX}px; max-width: ${CELL_WIDTH_PX}px; width: ${CELL_WIDTH_PX}px;`}
`;

// Absolute positioned fill inside a StyledRowCell. Stops CELL_BOTTOM_GAP_PX above the cell's bottom so the white
// box-shadow band on the parent shows through as a row separator.
type StyledRecommendationCellProps = {
    backgroundColor: string;
};

export const StyledRecommendationCell = styled('div', {
    shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<StyledRecommendationCellProps>`
    position: absolute;
    inset: 0 0 ${CELL_BOTTOM_GAP_PX}px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${CELL_FONT_SIZE};
    color: ${NEUTRAL_BLUE};
    background-color: ${({ backgroundColor }) => backgroundColor};
`;

// When `isDisabled`, each cell gets a white-wash ::after overlay
type StyledInteractiveRowProps = {
    isDisabled?: boolean;
};

// Clickable row with hover + selected highlight:
// Swaps the first cell's (gray) fill to the darker active gray.
// Colored cells aren't touched so their tier color stays recognizable.
export const StyledInteractiveRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'isDisabled',
})<StyledInteractiveRowProps>`
    cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'pointer')};
    ${({ isDisabled }) =>
        !isDisabled &&
        `
        &:hover > td,
        &.Mui-selected > td {
            background-color: ${ROW_ACTIVE_BACKGROUND_COLOR};
        }
    `}
    ${({ isDisabled }) =>
        isDisabled &&
        `
        & > td::after {
            content: '';
            position: absolute;
            inset: 0;
            background-color: rgba(255, 255, 255, 0.5);
            pointer-events: none;
        }
    `}
`;
